// lib/leasing/unprompted-turns.ts
//
// A TURN NOBODY ASKED FOR MAY READ. IT MAY NOT WRITE.
//
// The Source setup panel reacts to the form: pick an asset and the agent says a word about that
// asset. The reaction is wanted. What happened on 2026-08-15 is not. A tester picked "Espace Saint
// Quentin" from the Assets dropdown at 16:16:15, typed nothing at all, and at 16:17:01 the agent
// set two production Lead rows on a two day old campaign to INTERESTING and DISMISSED, then carried
// the stolen lead into the new campaign's closing verdict. The mode selector read Manual throughout,
// the panel's own copy says "Loading fills this form; it does not launch", and the instruction the
// reaction shipped with ended on "do not run any command". Three sentences,
// none of them a brake.
//
// So the boundary is this list, checked by the command runner before it executes anything, on the
// turn the panel opened AND on every follow-up turn that turn spawns (the write above was three
// turns downstream of the click, so gating the first turn alone would have caught nothing).
//
// AN ALLOW LIST, ON PURPOSE. Forgetting to add a new command to a deny list writes silently to a
// client's data. Forgetting to add one here costs one refused reaction that says why, in a turn the
// user did not ask for. Those are not comparable.
//
// WHAT BELONGS HERE: reading, and showing what was read. Nothing that spends credits, changes a
// record, moves the user's screen, or starts a run.
export const UNPROMPTED_TURN_ALLOWED_ACTION_TYPES: ReadonlySet<string> = new Set([
  'QUERY',
  'WEB_SEARCH',
  // Reads the asset's vacancies, tenant mix and past runs in one call; writes nothing.
  'DIAGNOSE_ASSET_FOR_SOURCING',
  // Writes only the session's own working memory (anchor masses), which is how the agent keeps the
  // asset it is reacting to in context. No client record is touched.
  'MANAGE_CONTEXT',
  // ASSISTANT_ALERT looks harmless and is not: its handler speaks the alert out loud through
  // /api/tts, so on an account with audio on it would make the app talk because a dropdown was
  // touched, and bill a provider call for it.
]);

/** May this command run in a turn the user did not ask for? Anything unnamed is refused. */
export const commandAllowedInUnpromptedTurn = (actionType: unknown): boolean =>
  typeof actionType === 'string' && UNPROMPTED_TURN_ALLOWED_ACTION_TYPES.has(actionType);

/**
 * How many turns deep an app-started chain may go. Two: one to look something up, one to say what
 * it found. Never a third, because a reaction is a sentence and not a piece of work.
 *
 * On 2026-08-15 there was no limit at all. The reaction to one asset being picked answered with a
 * QUERY and autoRecall: true, a Source session auto-recalls unconditionally, and the chain woke
 * itself nine times in two and a half minutes, each turn started by the previous one finishing.
 * Twelve agent turns at 1 credit around a run whose whole budget was 5, on a card advertising 5.
 */
export const UNPROMPTED_TURN_MAX_DEPTH = 2;

/**
 * May the agent be woken again, given how deep the current chain already is?
 *
 * Depth 0 means the user asked for this turn: no opinion, it keeps the autonomous multi-pass loop
 * it needs to do real work. This is the whole reason the rule is expressed as a depth rather than
 * as a global cap.
 */
export const unpromptedChainMayContinue = (depth: number | undefined): boolean =>
  !depth || depth < UNPROMPTED_TURN_MAX_DEPTH;

/**
 * What the user reads in the command trace when a step is refused. Written for them, not for the
 * agent: a refused command marks the script failed, which suppresses the follow-up turn, so nothing
 * reads this back and paraphrases it. The person looking at the trace is the only reader.
 */
export const unpromptedTurnRefusal = (actionType: string): string =>
  `Step refused: "${actionType}" changes data, and this turn was the assistant reacting to the ` +
  `campaign form on its own, not something you asked for. Ask for it and it will run.`;

/**
 * DID A HUMAN ASK FOR A BILLED RUN IN THE TURN NOW RUNNING?
 *
 * A launch spends one credit per brand and cannot be undone once the worker starts scoring. On
 * 2026-08-08 the agent launched a 50-brand run in a turn where it twice told the user it was NOT
 * launching, and nothing stopped it: the approval card was removed in August, so the only brake was
 * a sentence of doctrine asking the model not to. A sentence is not a brake.
 *
 * THE DEPTH ABOVE CANNOT ANSWER THIS, which is why both ship, and the reasons are worth keeping.
 * Depth reads 0 for "set up the sourcing parameters for Flora" exactly as it reads 0 for "launch
 * it", and the 2026-08-08 run was launched inside a chain the user did start, so depth alone would
 * have let it through. Depth 0 does not even mean a human started the turn: the campaign-landing
 * recall in components/portal/SourcingCenterView.tsx wakes the agent on a socket event and scores 0. Depth stops the setup panel's chain from writing; these words
 * decide whether a person asked for a billed run.
 *
 * Deliberately a word test rather than a model call: it must be cheap, synchronous and impossible to
 * talk out of. It errs toward refusing, because a refused launch costs one sentence asking the user
 * and a wrong launch costs their credits and their trust.
 */
export const LAUNCH_INTENT = /\b(launch|launches|launching|lance|lancer|lance-?la|run it|run a|start (a|the)|source (it|this|the)|go ahead|do it|yes,? (do|go|launch)|vas-?y|démarre|demarre)\b/i;

/**
 * Reads the words the user typed for the turn now running, which are empty whenever nobody typed
 * any: `_executeDealerTurn` rewrites them every turn and fills them only where `askAssistant` says a
 * person wrote into the composer.
 */
export const userAskedToLaunch = (lastUserTurnPrompt: unknown): boolean =>
  typeof lastUserTurnPrompt === 'string' && LAUNCH_INTENT.test(lastUserTurnPrompt.trim());

/**
 * DID A HUMAN ASK FOR CONTACTS IN THE TURN NOW RUNNING?
 *
 * Same brake as the launch guard above, for the same reason: contact discovery bills per brand,
 * and the agent keeps running it on its own initiative at the end of work the user DID ask for.
 * Measured three times - after a pass on 2026-08-16, after /verify-stores on 2026-08-19, after an
 * extension on 2026-08-20, each time on brands it had just decided were interesting.
 *
 * DOCTRINE ALREADY FORBADE IT, TWICE, IN CAPITALS ("NEVER ON YOUR OWN INITIATIVE, AND NEVER AS THE
 * NEXT THING AFTER A SOURCING PASS"), and it happened anyway. A third sentence would be the third
 * try at the same failed idea: a sentence is not a brake. This is the brake.
 *
 * The turns where it fires are the ones nobody typed into: a campaign landing wakes the agent
 * through the recall in SourcingCenterView, so `lastUserTurnPrompt` is empty and every word test
 * below fails. That is the whole mechanism - it does not try to judge intent, it checks that a
 * person was there.
 *
 * /fill-data is in because it runs /fill-contacts by definition: typing it IS the request.
 *
 * Agreement words are in on purpose: the rule the agent is given is to OFFER contacts in one
 * sentence and wait, so "go ahead" is the expected way this gets asked for, and refusing it would
 * make the offer a dead end. Not shared with LAUNCH_INTENT: that regex guards a bigger spend and is
 * not worth touching to save four words.
 */
export const CONTACTS_INTENT =
  /\b(contacts?|contacter|interlocuteurs?|d[ée]cideurs?|decision[- ]?makers?|coordonn[ée]es|qui (appeler|contacter)|who to (call|contact|reach)|expansion manager|go ahead|do it|yes,? (do|go)|vas-?y|oui|fill-data)\b/i;

/**
 * Reads the words the user typed for the turn now running. Empty whenever nobody typed any, which
 * is exactly the case this exists to refuse.
 */
export const userAskedForContacts = (lastUserTurnPrompt: unknown): boolean =>
  typeof lastUserTurnPrompt === 'string' && CONTACTS_INTENT.test(lastUserTurnPrompt.trim());
